/**
 * V2 category browser — V1's "Discover" picker as a real page
 * (/adventure/categories): the FULL catalog, browsable as a tree and
 * filtered instantly on every keystroke (the catalog is cached locally,
 * so no debounce, no spinner). The parks toggle lives here — its actual
 * job is trimming category noise from this listing (user feedback,
 * 2026-07-02). Follow inline; names open the category page.
 *
 * Pre-flip stragglers round (2026-07-11): the All/Following scope pills,
 * branch-preserving search results (matches keep their ancestors, like
 * V1's picker), and V1's "Popular Categories" tile grid for signed-in
 * explorers who follow nothing yet (category-picker.js:686-741).
 */
import * as gateway from '../../core/gateway.js';
import {
  fetchAllCategories,
  categorySlug,
  filterStateParks,
  getShowStateParks,
  setShowStateParks,
  searchVisibleIds,
} from '../../core/categories.js';
import { icon, categoryIcon } from '../../ui/icons.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { getUrlParam } from '../../ui/share.js';
import { isFollowingCategory, followCategory, unfollowCategory } from '../follows/follows.js';

function followBtn(store, cat) {
  if (!store.get().user) return '';
  const following = isFollowingCategory(store, cat.id);
  return `
    <button type="button" class="browse-follow ${following ? 'is-following' : ''}"
      data-follow-cat="${cat.id}" aria-pressed="${following}">
      ${following ? 'Following' : 'Follow'}
    </button>`;
}

function row(store, cat) {
  return `
    <div class="browse-row">
      <button type="button" class="browse-name" data-open-cat="${categorySlug(cat)}">
        ${categoryIcon(cat, 16)}<span>${escapeHtml(cat.name)}</span>
      </button>
      ${followBtn(store, cat)}
    </div>`;
}

/**
 * Recursive tree of <details>. `visible` limits it to a search's
 * branches (matches + ancestors + descendants), `openAll` unfolds them
 * so results are seen in tree context, not as a flat list (V1 picker).
 * Exported for tests.
 */
export function treeHtml(store, cats, parentId = null, { visible = null, openAll = false } = {}) {
  const level = cats
    .filter((c) => (c.parent_id || null) === parentId)
    .filter((c) => !visible || visible.has(c.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  if (!level.length) return '';
  return level
    .map((cat) => {
      const kids = treeHtml(store, cats, cat.id, { visible, openAll });
      if (!kids) return row(store, cat);
      // Parents get BOTH affordances (2026-07-12 feedback: "Bar" only
      // expanded — no way to visit it): the name navigates to the
      // category page, the caret (and the rest of the row) expands.
      return `
        <details class="browse-branch" ${openAll ? 'open' : ''}>
          <summary>
            <button type="button" class="browse-name" data-open-cat="${categorySlug(cat)}">
              ${categoryIcon(cat, 16)}<span>${escapeHtml(cat.name)}</span>
            </button>
            ${followBtn(store, cat)}
            <span class="browse-caret" aria-hidden="true">▸</span>
          </summary>
          <div class="browse-children">${kids}</div>
        </details>`;
    })
    .join('');
}

/** V1's popular tiles: top categories by activity, tap to follow. */
async function fetchPopular() {
  const { data, error } = await gateway.rpc('get_top_categories_by_yoinks', { limit_count: 13 });
  if (error || !data) return [];
  return data
    .filter((row) => (row.category_name || '').toLowerCase() !== 'uncategorized')
    .slice(0, 12)
    .map((row) => ({
      id: row.category_id,
      name: row.category_name || 'Unknown',
      count: row.yoink_count,
    }));
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  let all = [];
  let popular = [];
  // Discover's "N parks also match — browse them" row lands here with the
  // term and ?parks=1: parks shown for THIS visit only, the stored toggle
  // untouched (2026-09-15). Unticking the box ends the override.
  let term = getUrlParam('q') || '';
  let parksThisVisit = getUrlParam('parks') === '1';
  let scope = 'all'; // 'all' | 'following' (V1 picker's sections as pills)
  let showPopular = false; // decided once at load: 0 follows → onboarding grid
  let followedViaTile = false; // grid follows keep the grid up (Let's go!)
  let destroyed = false;

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  function showParks() {
    return parksThisVisit || getShowStateParks();
  }

  function visible() {
    return filterStateParks(all, all, showParks());
  }

  function followedSet() {
    return new Set(store.get().followedCategoryIds || []);
  }

  /**
   * New explorers (signed in, following nothing) get V1's Popular grid:
   * big tappable follow tiles, then a "Let's go!" into the Following feed.
   */
  function popularHtml() {
    // Sticky for the visit: following a tile must not vanish the grid
    // mid-flow (the "Let's go!" button appears in its place)
    if (!showPopular || !popular.length || scope !== 'all' || term.trim()) return '';
    const followed = followedSet();
    return `
      <section class="browse-popular" data-popular-grid aria-label="Popular categories">
        <h3 class="friends-title">${icon('flame', 16)} Popular categories</h3>
        <p class="log-hint">Tap to follow — pick a few to get started!</p>
        <div class="popular-grid">
          ${popular.map((cat) => {
            const isFollowed = followed.has(cat.id);
            return `
              <button type="button" class="popular-tile ${isFollowed ? 'is-followed' : ''}"
                data-follow-cat="${cat.id}" aria-pressed="${isFollowed}"
                aria-label="${isFollowed ? 'Unfollow' : 'Follow'} ${escapeHtml(cat.name)}">
                ${isFollowed ? '<span class="popular-check">✓</span>' : ''}
                <span class="popular-icon">${categoryIcon(cat, 30)}</span>
                <span class="popular-name">${escapeHtml(cat.name)}</span>
                <span class="popular-count">${cat.count} yoinks</span>
              </button>`;
          }).join('')}
        </div>
        ${followed.size > 0 ? '<button type="button" class="v2-btn-leather popular-go" data-lets-go>Let’s go!</button>' : ''}
      </section>`;
  }

  function scopePills() {
    if (!store.get().user) return '';
    const followedCount = followedSet().size;
    return `
      <div class="v2-scope" role="group" aria-label="Which categories">
        <button class="v2-scope-btn ${scope === 'all' ? 'scope-active' : ''}"
          data-browse-scope="all" aria-pressed="${scope === 'all'}">All</button>
        <button class="v2-scope-btn ${scope === 'following' ? 'scope-active' : ''}"
          data-browse-scope="following" aria-pressed="${scope === 'following'}">Following (${followedCount})</button>
      </div>`;
  }

  function listHtml() {
    let cats = visible();
    if (scope === 'following') {
      const followed = followedSet();
      const rows = cats
        .filter((c) => followed.has(c.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => row(store, c))
        .join('');
      return rows || '<p class="log-hint">You’re not following any categories yet — flip to All and tap Follow on the ones you collect.</p>';
    }
    const needle = term.trim();
    if (!needle) return treeHtml(store, cats);
    // Branch-preserving search (V1 picker): matches render inside their
    // tree with ancestors for context, branches unfolded
    const ids = searchVisibleIds(cats, needle);
    return treeHtml(store, cats, null, { visible: ids, openAll: true }) ||
      '<p class="log-hint">No category by that name.</p>';
  }

  function render() {
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
      <header class="v2-category-head">
        <div class="cat-head-names">
          <h2 class="cat-head-name">All categories</h2>
          <p class="cat-head-desc">Follow the ones you collect — new finds land in your Following feed.</p>
        </div>
      </header>
      ${popularHtml()}
      <div class="v2-toggles">${scopePills()}</div>
      <form class="v2-search" data-browse-form>
        <label class="visually-hidden" for="browse-search">Search categories</label>
        <input id="browse-search" type="search" name="browseSearch" placeholder="Search categories…"
          autocomplete="off" enterkeyhint="search" value="${escapeHtml(term)}">
        <button type="submit" class="v2-search-go">Search</button>
        ${term ? '<button type="button" class="v2-search-clear" data-browse-clear>Clear</button>' : ''}
      </form>
      <label class="v2-parks-toggle">
        <input type="checkbox" name="browseParks" ${showParks() ? 'checked' : ''}>
        Show parks &amp; protected areas
      </label>
      <div class="browse-list" data-browse-list>${listHtml()}</div>`;
  }

  function refreshList() {
    const list = el.querySelector('[data-browse-list]');
    if (list) list.innerHTML = listHtml();
  }

  function onInput(event) {
    // Only the parks toggle reacts live; text search waits for submit
    if (event.target.name === 'browseParks') {
      parksThisVisit = false;
      setShowStateParks(event.target.checked);
      refreshList();
    }
  }

  function onSubmit(event) {
    if (!event.target.closest('[data-browse-form]')) return;
    event.preventDefault();
    term = el.querySelector('input[name="browseSearch"]').value;
    refreshList();
  }

  async function onClick(event) {
    if (event.target.closest('[data-browse-clear]')) {
      term = '';
      render();
      return;
    }
    const scopeBtn = event.target.closest('[data-browse-scope]');
    if (scopeBtn) {
      if (scopeBtn.dataset.browseScope !== scope) {
        scope = scopeBtn.dataset.browseScope;
        render();
      }
      return;
    }
    if (event.target.closest('[data-lets-go]')) {
      store.set({ feedScope: 'following', categoryFilter: null });
      router.navigate('discover');
      return;
    }
    const open = event.target.closest('[data-open-cat]');
    if (open) {
      // Parents live inside a <summary> — don't ALSO toggle the branch
      event.preventDefault();
      router.navigate('category', { slug: open.dataset.openCat });
      return;
    }
    const followEl = event.target.closest('[data-follow-cat]');
    if (followEl) {
      event.preventDefault(); // summaries shouldn't toggle on follow taps
      const id = followEl.dataset.followCat;
      // Flag BEFORE the await: the store notifies mid-follow, and the
      // subscriber must not read this as "veteran's follows arrived"
      const fromGrid = Boolean(followEl.closest('[data-popular-grid]'));
      if (fromGrid) followedViaTile = true;
      try {
        if (isFollowingCategory(store, id)) await unfollowCategory(store, id);
        else await followCategory(store, id);
      } catch {
        // button re-renders in its previous state
      }
      if (destroyed) return;
      // Popular tiles need the whole section refreshed (the ✓ and the
      // Let's-go button ride the follow state); rows just re-list
      if (fromGrid) render();
      else refreshList();
    }
  }

  el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
  document.title = 'Categories | Yoink Adventures v2';
  Promise.all([
    fetchAllCategories(),
    fetchPopular().catch(() => []),
  ])
    .then(([cats, top]) => {
      if (destroyed) return;
      all = cats;
      popular = top;
      showPopular = Boolean(store.get().user) && followedSet().size === 0;
      render();
    })
    .catch(() => {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    });

  const unsubscribe = store.subscribe(() => {
    if (destroyed || !all.length) return;
    // Follow state can settle AFTER the catalog: a veteran explorer's
    // follows arriving late must retract the onboarding grid
    if (showPopular && !followedViaTile && followedSet().size > 0) {
      showPopular = false;
      render();
      return;
    }
    refreshList();
  });

  el.addEventListener('input', onInput);
  el.addEventListener('submit', onSubmit);
  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    unsubscribe();
    el.removeEventListener('input', onInput);
    el.removeEventListener('submit', onSubmit);
    el.removeEventListener('click', onClick);
  };
}
